import React from "react";
import { Link } from "react-router-dom";

/**
 * Rendu du balisage inline utilisé dans les articles :
 *   **gras**   *italique*   `code`   [texte](https://url)   [texte](/blog/slug)   [^3]  (appel de référence n° 3)
 *
 * Volontairement minimaliste (pas de dépendance Markdown) : suffisant pour des articles de blog,
 * prévisible, et sans HTML injecté.
 */
const PATTERN = /(\*\*(.+?)\*\*)|(\*(.+?)\*)|(`([^`]+?)`)|(\[\^(\d+)\])|(\[([^\]]+?)\]\(([^)\s]+?)\))/g;

const isExternal = href => /^https?:\/\//i.test(href) || /^mailto:/i.test(href);

/** Défilement doux vers un id, en conservant le lien natif (#ref-3) pour l'accessibilité et le no-JS. */
export const scrollToId = (id, event) => {
  const target = typeof document !== "undefined" ? document.getElementById(id) : null;
  if (!target) return;
  if (event) event.preventDefault();
  target.scrollIntoView({ behavior: "smooth", block: "start" });
  // Conserve l'ancre dans l'URL sans re-déclencher un saut brutal.
  if (typeof window !== "undefined" && window.history && window.history.replaceState) {
    window.history.replaceState(null, "", `#${id}`);
  }
  // Rend l'élément focalisable pour les lecteurs d'écran, sans laisser de tabindex permanent.
  if (!target.hasAttribute("tabindex")) {
    target.setAttribute("tabindex", "-1");
    target.addEventListener("blur", () => target.removeAttribute("tabindex"), { once: true });
  }
  target.focus({ preventScroll: true });
};

/**
 * @param {string} text  Texte avec balisage inline.
 * @param {object} options
 * @param {Set<string>} [options.seenCitations]  Si fourni, la PREMIÈRE occurrence de chaque [^n] reçoit id="cite-n"
 *                                                (utilisé par la liste des références pour le lien de retour).
 * @returns {React.ReactNode[]}
 */
export function renderInline(text, options = {}) {
  if (text == null || text === "") return null;
  const source = String(text);
  const re = new RegExp(PATTERN.source, "g"); // instance locale : la récursion ne partage pas lastIndex
  const nodes = [];
  let last = 0;
  let key = 0;
  let match;

  while ((match = re.exec(source)) !== null) {
    if (match.index > last) nodes.push(source.slice(last, match.index));

    if (match[2] !== undefined) {
      nodes.push(<strong key={key++}>{renderInline(match[2], options)}</strong>);
    } else if (match[4] !== undefined) {
      nodes.push(<em key={key++}>{renderInline(match[4], options)}</em>);
    } else if (match[6] !== undefined) {
      nodes.push(<code key={key++}>{match[6]}</code>);
    } else if (match[8] !== undefined) {
      const n = match[8];
      const { seenCitations } = options;
      const isFirst = seenCitations ? !seenCitations.has(n) : false;
      if (seenCitations) seenCitations.add(n);
      nodes.push(
        <sup key={key++} className="cite" id={isFirst ? `cite-${n}` : undefined}>
          <a href={`#ref-${n}`} onClick={e => scrollToId(`ref-${n}`, e)} aria-label={`Voir la référence ${n}`}>
            [{n}]
          </a>
        </sup>
      );
    } else if (match[10] !== undefined) {
      const label = match[10];
      const href = match[11];
      if (isExternal(href)) {
        nodes.push(
          <a key={key++} href={href} target="_blank" rel="noopener noreferrer">
            {renderInline(label, options)}
          </a>
        );
      } else if (href.startsWith("#")) {
        nodes.push(
          <a key={key++} href={href} onClick={e => scrollToId(href.slice(1), e)}>
            {renderInline(label, options)}
          </a>
        );
      } else {
        nodes.push(
          <Link key={key++} to={href}>
            {renderInline(label, options)}
          </Link>
        );
      }
    }
    last = re.lastIndex;
  }

  if (last < source.length) nodes.push(source.slice(last));
  return nodes;
}

export default renderInline;
